import React from "react";
import styles from "./index.module.scss";
import Button from "@mui/material/Button";
import GeneralDialog from "../../core/GeneralDialog";
import DateCell from "../../core/DateCell";
import InputCell from "../../core/InputCell";
import CloseIcon from '@mui/icons-material/Close';
import { InvStatus, ShippingCoType, ShippingTypeOptions } from "../../../libs/constants";
import { formatNumber, str2number } from "../../../libs/helpers";
import { InvoiceMasterData } from "../../../apis/models/InvoiceMasterRequest";
import DateWithoutTime from "../../../libs/dateWithoutTime";
import SelectBoxCell from "../../core/SelectBoxCell";
import ShippingFeeDialog from "../../dialogs/invoiceDetails/ShippingFeeDialog";
import { ShippingFeeDialogItem } from "../../../libs/types";

type InputField = {
  uniqueId: string;
  isSagawa: 0 | 1;
  isChakubarai: 0 | 1;
  managementNo: string | null;
  postcode: string | null;
  referenceNo: string;
  dateAdded: DateWithoutTime;
  quantity: string;
  unitPrice: string;
  actualShippingFee: string;
  memo: string;
}

type ErrorInfo = {
  dateAdded?: string;
  quantity?: string;
  unitPrice?: string;
  actualShippingFee?: string;
}

export type ShippingFeeTabType = InputField & {
  error: ErrorInfo
}

const ShippingFee: React.FC<ShippingFeeProps> = (props: ShippingFeeProps) => {
  const [openWarningDialog, setOpenWarningDialog] =React.useState<boolean>(false);
  const [openAddDialog, setOpenAddDialog] = React.useState<boolean>(false);

  const onRowChange = (rowItem: ShippingFeeTabType) => {
    const newArray = [...props.tabValues];
    const dataIndex = newArray.findIndex(item => item.uniqueId === rowItem.uniqueId);
    validateRow(rowItem);
    newArray.splice(dataIndex, 1, rowItem);
    props.onChange(newArray);
  }

  const onRowDelete = (uniqueId: string) => {
    props.onChange(props.tabValues.filter(rowItem => rowItem.uniqueId !== uniqueId));
  }

  const handleSaveDialog = (
    items: ShippingFeeDialogItem[],
    type: ShippingCoType.yCOD | ShippingCoType.yPIA | ShippingCoType.sCOD | ShippingCoType.sPIA
  ) => {
    const isSagawa = type === ShippingCoType.sCOD || type === ShippingCoType.sPIA ? 1 : 0;
    const isChakubarai = type === ShippingCoType.yCOD || type === ShippingCoType.sCOD ? 1 : 0;
    const uniquePrefix = `dialog-${new Date().getTime()}`;

    const addItems = items.map((item, index) => {
      const uniqueId = `${uniquePrefix}-${index}`;
      return {
        uniqueId: uniqueId,
        isSagawa: isSagawa,
        isChakubarai: isChakubarai,
        managementNo: item.managementNo,
        postcode: item.postcode,
        referenceNo: item.referenceNo,
        dateAdded: item.dateAdded,
        quantity: String(item.quantity),
        unitPrice: String(item.unitPrice),
        actualShippingFee: String(item.actualShippingFee),
        memo: item.memo,
        error: {}
      } as ShippingFeeTabType
    })
    props.onChange(props.tabValues.concat(addItems));
    setOpenAddDialog(false);
  }

  const validateRow = (rowItem: ShippingFeeTabType) => {
    const error: ErrorInfo = {
      dateAdded: validateDateAdded(rowItem.dateAdded),
      quantity: validateNumber(rowItem.quantity),
      unitPrice: validateNumber(rowItem.unitPrice),
      actualShippingFee: validateNumber(rowItem.actualShippingFee),
    }
    rowItem.error = error;
  }

  const validateDateAdded = (value: DateWithoutTime): string | undefined => {
    const dateString = value.getValue4Display()
    if (value.getValue4Display().length === 0) {
      return "入力必須です"
    } else if (value.isInvalid()) {
      return "年月日で入力してください"
    } else {
      const invoiceFrom = new DateWithoutTime(props.targetInvoice.from)
      const invoiceTo = new DateWithoutTime(props.targetInvoice.to)
      if (dateString < invoiceFrom.getValue4Display() || invoiceTo.getValue4Display() < dateString) {
        return "請求期間の範囲で入力してください"
      }
    }
    return undefined
  }

  const validateNumber = (value: string) => {
    if (value.length === 0) {
      return "入力必須です"
    } else if (isNaN(+value) || parseInt(value) < 1) {
      return "１以上を入力してください"
    }
    return undefined
  }

  const isConfirmed = React.useMemo(() => {
    return props.targetInvoice?.status === InvStatus.CONFIRMED || props.targetInvoice?.status === InvStatus.EXPORT;
  }, [props.targetInvoice]);

  const viewData = React.useMemo(() => {
    // console.log(props.tabValues);
    const sagawaMotobarai = props.tabValues.filter(item => item.isSagawa && !item.isChakubarai);
    const sagawaChakubarai = props.tabValues.filter(item => item.isSagawa && item.isChakubarai);
    const yamatoMotobarai = props.tabValues.filter(item => !item.isSagawa && !item.isChakubarai);
    const yamatoChakubarai = props.tabValues.filter(item => !item.isSagawa && item.isChakubarai);
    return {sagawaMotobarai, sagawaChakubarai, yamatoMotobarai, yamatoChakubarai}
  }, [props.tabValues]);

  const shippingExistDates = React.useMemo(() => {
    const existDates: DateWithoutTime[] = [];
    props.tabValues.forEach(d => {
      if (existDates.findIndex(ed => ed.equals(d.dateAdded)) >= 0) {
        return
      }
      existDates.push(d.dateAdded)
    });

    return existDates
  }, [props.tabValues])

  return (
    <div className={styles.container}>
      <div className={styles.edit_functions}>
        {isConfirmed ? 
          <Button variant="outlined" color="primary" onClick={() => setOpenWarningDialog(true)}>編集</Button> : 
          <>
            {/* <Button variant="outlined" onClick={() => onOpenBatchDelete()}>運賃費一括削除</Button> */}
            <Button variant="outlined" onClick={() => setOpenAddDialog(true)}>＋項目追加</Button>
            <Button variant="outlined" onClick={() => props.onClickUpload()}>運賃費アップロード</Button>
          </>
        }
      </div>

      <div className={styles.table_section}>
        <div>
          <div className={styles.table_section__title}>佐川急便運賃</div>

          <div className={styles.table_section__sub_title}>元払い</div>
          <div className={styles.table_wrapper}>
            <table>
              <thead>
                <TableHeaderRow fullSize isConfirmed={isConfirmed} />
              </thead>
              <tbody>
                {
                  viewData.sagawaMotobarai.map(rowItem => (
                    <TableBodyRow
                      fullSize
                      key={rowItem.uniqueId}
                      uniqueId={rowItem.uniqueId}
                      isConfirmed={isConfirmed}
                      rowItem={rowItem}
                      onChange={(val) => onRowChange(val)}
                      onDelete={() => onRowDelete(rowItem.uniqueId)}
                    />
                  ))
                }
                {
                  viewData.sagawaMotobarai.length === 0 &&
                  <tr>
                    <td colSpan={12}>データがありません</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>

          <div className={styles.table_section__sub_title}>着払い</div>
          <div className={styles.table_wrapper}>
            <table>
              <thead>
                <TableHeaderRow isConfirmed={isConfirmed} />
              </thead>
              <tbody>
                {
                  viewData.sagawaChakubarai.map(rowItem => (
                    <TableBodyRow
                      key={rowItem.uniqueId}
                      uniqueId={rowItem.uniqueId}
                      isConfirmed={isConfirmed}
                      rowItem={rowItem}
                      onChange={(val) => onRowChange(val)}
                      onDelete={() => onRowDelete(rowItem.uniqueId)}
                    />
                  ))
                }
                {
                  viewData.sagawaChakubarai.length === 0 &&
                  <tr>
                    <td colSpan={10}>データがありません</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        </div>

        <div>
          <div className={styles.table_section__title}>ヤマト運賃</div>

          <div className={styles.table_section__sub_title}>元払い</div>
          <div className={styles.table_wrapper}>
            <table>
              <thead>
                <TableHeaderRow isConfirmed={isConfirmed} />
              </thead>
              <tbody>
                {
                  viewData.yamatoMotobarai.map(rowItem => (
                    <TableBodyRow
                      key={rowItem.uniqueId}
                      uniqueId={rowItem.uniqueId}
                      isConfirmed={isConfirmed}
                      rowItem={rowItem}
                      onChange={(val) => onRowChange(val)}
                      onDelete={() => onRowDelete(rowItem.uniqueId)}
                    />
                  ))
                }
                {
                  viewData.yamatoMotobarai.length === 0 &&
                  <tr>
                    <td colSpan={10}>データがありません</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>

          <div className={styles.table_section__sub_title}>着払い</div>
          <div className={styles.table_wrapper}>
            <table>
              <thead>
                <TableHeaderRow isConfirmed={isConfirmed} />
              </thead>
              <tbody>
                {
                  viewData.yamatoChakubarai.map(rowItem => (
                    <TableBodyRow
                      key={rowItem.uniqueId}
                      uniqueId={rowItem.uniqueId}
                      isConfirmed={isConfirmed}
                      rowItem={rowItem}
                      onChange={(val) => onRowChange(val)}
                      onDelete={() => onRowDelete(rowItem.uniqueId)}
                    />
                  ))
                }
                {
                  viewData.yamatoChakubarai.length === 0 &&
                  <tr>
                    <td colSpan={10}>データがありません</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>

        </div>
      </div>
      <GeneralDialog
        open={openWarningDialog}
        onCancel={() => setOpenWarningDialog(false)}
        cancelText="キャンセル"
        onSave={() => {
          props.onNotYetConfirmed();
          setOpenWarningDialog(false);
        }}
        saveText="確定を取り消す"
        title="請求確定取り消し"
        onClose={() => setOpenWarningDialog(false)}
      >
        <div>
          既に確定済の請求です。<br/>
          内容を編集するには請求の確定取り消しが必要です。<br/><br/>
        </div>
        <div>請求の確定を取り消しますか？</div>
      </GeneralDialog>
      {!isConfirmed ? 
        <div className={styles.save_functions}>
          <Button variant="outlined" color="primary" onClick={() => props.onReset()} disabled={!props.isChanged}>編集内容を破棄</Button>
          <Button variant="contained" color="secondary" onClick={() => props.onSave()} disabled={!props.isChanged}>保存</Button>
        </div>
       : <></>
      }
      {
        openAddDialog ? (
          <ShippingFeeDialog
            targetBrand={props.targetInvoice.brand}
            targetInvoice={props.targetInvoice}
            open={openAddDialog} 
            onClose={() => setOpenAddDialog(false)}
            onSave={(val, type) => handleSaveDialog(val, type)}
            existDates={shippingExistDates}
          />
        ) : (
          <></>
        )
      }
    </div>
  );
};

export default ShippingFee;

export type ShippingFeeProps = {
  targetInvoice: InvoiceMasterData;
  tabValues: ShippingFeeTabType[];
  isChanged: boolean;
  onNotYetConfirmed: () => void;
  onReset: () => void;
  onSave: () => void;
  onChange: (val: ShippingFeeTabType[]) => void;
  onClickUpload: () => void;
};

const TableHeaderRow = (props: {fullSize?: boolean; isConfirmed: boolean}) => {
  return (
    <tr>
      <td style={{width: "102px"}}>伝票種別</td>
      {props.fullSize ? (
          <>
            <td style={{width: "150px"}}>管理番号</td>
            <td style={{width: "100px"}}>郵便番号</td>
          </>
        ) : (
          <></>
        )
      }
      <td style={{width: "150px"}}>問い合わせNo.</td>
      <td style={{width: "145px"}}>日付</td>
      <td style={{width: "70px"}}>個数</td>
      <td style={{width: "70px"}}>運賃単価</td>
      <td style={{width: "80px"}}>運賃合計</td>
      <td style={{width: "80px"}}>実運賃</td>
      <td style={{width: "80px"}}>差額</td>
      <td style={{width: "150px"}}>備考</td>
      {!props.isConfirmed ? <td style={{width: "30px"}}></td> : <></>}
    </tr>
  )
}

const TableBodyRow = (
  props: {
    key: React.Key;
    uniqueId: string;
    fullSize?: boolean;
    rowItem: ShippingFeeTabType; 
    isConfirmed: boolean;
    onChange: (rowItem: ShippingFeeTabType) => void;
    onDelete: () => void;
  }) => {

    const editableCellStyle = React.useMemo(() => {
      return !props.isConfirmed ? `${styles.unconfirmed}` : "";
    }, [props.isConfirmed]);

    const handleChange = (rowItem: ShippingFeeTabType) => {
      props.onChange(rowItem);
    }

    return (
      <tr key={props.uniqueId}>
        <td className={editableCellStyle}>
          <SelectBoxCell
            isConfirmed={props.isConfirmed}
            options={ShippingTypeOptions}
            value={String(props.rowItem.isChakubarai)}
            onChange={(val) => handleChange({...props.rowItem, isChakubarai: String(val) === "1" ? 1 : 0})}
          />
        </td>
        {props.fullSize ? (
            <>
              <td className={styles.colorPrimary}>
                {props.rowItem.managementNo ?? ""}
              </td>
              <td className={editableCellStyle}>
                <InputCell
                  type="text"
                  isConfirmed={props.isConfirmed}
                  onChangeText={(val) => handleChange({...props.rowItem, postcode: String(val)})}
                  value={props.rowItem.postcode ?? ""}
                />
              </td>
            </>
          ) : (
            <></>
          )
        }
        <td className={editableCellStyle}>
          <InputCell
            type="text"
            isConfirmed={props.isConfirmed}
            onChangeText={(val) => handleChange({...props.rowItem, referenceNo: String(val)})}
            value={props.rowItem.referenceNo ?? ""}
          />
        </td>
        <td className={editableCellStyle}>
          <DateCell
            isConfirmed={props.isConfirmed}
            onChangeDate={(val) => handleChange({...props.rowItem, dateAdded: new DateWithoutTime(val)})}
            value={props.rowItem.dateAdded.getValue4Display()}
            />
        </td>
        <td className={editableCellStyle}>
          <InputCell
            type="number"
            isConfirmed={props.isConfirmed}
            onChangeText={(val) => handleChange({...props.rowItem, quantity: String(val)})}
            value={props.rowItem.quantity ?? ""}
          />
        </td>
        <td className={editableCellStyle}>
          <InputCell
            type="number"
            isConfirmed={props.isConfirmed}
            onChangeText={(val) => handleChange({...props.rowItem, unitPrice: String(val)})}
            value={props.rowItem.unitPrice ?? ""} 
          />
        </td>
        <td className={styles.colorPrimary}>
          {formatNumber(str2number(props.rowItem.quantity) * str2number(props.rowItem.unitPrice))}
        </td>
        <td className={editableCellStyle}>
          <InputCell
            type="number"
            isConfirmed={props.isConfirmed}
            onChangeText={(val) => handleChange({...props.rowItem, actualShippingFee: String(val)})}
            value={props.rowItem.actualShippingFee ?? ""} 
          />
        </td>
        <td className={styles.colorPrimary}>
          {formatNumber(str2number(props.rowItem.quantity) * str2number(props.rowItem.unitPrice) - str2number(props.rowItem.actualShippingFee))}
        </td>
        <td className={editableCellStyle}>
          <InputCell
            type="text"
            isConfirmed={props.isConfirmed}
            onChangeText={(val) => handleChange({...props.rowItem, memo: String(val)})}
            value={props.rowItem.memo ?? ""}
          />
        </td>
        {!props.isConfirmed ? <td><CloseIcon className={styles.close_icon} onClick={() => props.onDelete()} color="primary"/></td> : <></>}
      </tr>
    )
}
